/**
 * Configuración de Multi-Moneda para Curvia AI
 * Soporta CLP (Chile) y USD (Internacional)
 */

export type Currency = 'CLP' | 'USD';

export interface CurrencyConfig {
  code: Currency;
  symbol: string;
  name: string;
  decimals: number;
  format: (amount: number) => string;
}

// Configuraciones de moneda
export const CURRENCIES: Record<Currency, CurrencyConfig> = {
  CLP: {
    code: 'CLP',
    symbol: '$',
    name: 'Peso Chileno',
    decimals: 0,
    format: (amount: number) => `$${Math.round(amount).toLocaleString('es-CL')} CLP`
  },
  USD: {
    code: 'USD',
    symbol: '$',
    name: 'Dólar Estadounidense',
    decimals: 2,
    format: (amount: number) => `$${amount.toFixed(2)} USD`
  }
};

// Tasa de conversión USD -> CLP (aproximada, actualizar periódicamente)
// Puedes obtenerla de una API en tiempo real
export const USD_TO_CLP_RATE = 865; // 1 USD ≈ 865 CLP (actualizado)

/**
 * Detecta la moneda preferida del usuario basándose en su ubicación
 * Usa la API de geolocalización del navegador como respaldo
 */
export async function detectUserCurrency(): Promise<Currency> {
  try {
    // 1. Intentar detectar por zona horaria
    const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
    if (timezone.includes('Santiago') || timezone.includes('Chile')) {
      return 'CLP';
    }

    // 2. Intentar detectar por idioma del navegador
    const language = navigator.language || (navigator as any).userLanguage;
    if (language && language.toLowerCase().includes('es-cl')) {
      return 'CLP';
    }

    // 3. Intentar usar API de geolocalización por IP (gratuita)
    try {
      const response = await fetch('https://ipapi.co/json/');
      const data = await response.json();

      if (data.country_code === 'CL') {
        return 'CLP';
      }
    } catch (err) {
      console.log('No se pudo obtener geolocalización por IP');
    }

    // 4. Por defecto, usar CLP (solo CLP por ahora)
    return 'CLP';
  } catch (error) {
    console.error('Error al detectar moneda del usuario:', error);
    // Por defecto, mostrar CLP
    return 'CLP';
  }
}

/**
 * Convierte un monto de una moneda a otra
 */
export function convertCurrency(amount: number, from: Currency, to: Currency): number {
  if (from === to) return amount;

  if (from === 'USD' && to === 'CLP') {
    return Math.round(amount * USD_TO_CLP_RATE);
  }

  if (from === 'CLP' && to === 'USD') {
    return Number((amount / USD_TO_CLP_RATE).toFixed(2));
  }

  return amount;
}

/**
 * Formatea un monto según la moneda especificada
 */
export function formatCurrency(amount: number, currency: Currency): string {
  return CURRENCIES[currency].format(amount);
}

/**
 * Obtiene la tasa de conversión actual desde una API externa (opcional)
 * Puedes implementar esto para obtener tasas en tiempo real
 */
export async function getExchangeRate(): Promise<number> {
  try {
    // Ejemplo usando una API gratuita de tasas de cambio
    const response = await fetch('https://api.exchangerate-api.com/v4/latest/USD');
    const data = await response.json();

    if (data.rates && data.rates.CLP) {
      return data.rates.CLP;
    }

    return USD_TO_CLP_RATE; // Fallback a tasa fija
  } catch (error) {
    console.error('Error al obtener tasa de cambio:', error);
    return USD_TO_CLP_RATE; // Fallback a tasa fija
  }
}

/**
 * Guarda la preferencia de moneda del usuario en localStorage
 */
export function saveCurrencyPreference(currency: Currency): void {
  localStorage.setItem('preferred_currency', currency);
}

/**
 * Obtiene la preferencia de moneda del usuario desde localStorage
 */
export function getCurrencyPreference(): Currency | null {
  const saved = localStorage.getItem('preferred_currency');
  return (saved === 'CLP' || saved === 'USD') ? saved : null;
}
